<template>
  <div id="bp" :style="mapStyle"></div>
</template>

<script>
import * as leaflet from "leaflet";
import zoomifyLayer from "../../model/zoomify.js";
import store from "@/store/index";
import { get } from "@/model/api";
import { baseUrl } from "@/model/http-common";

export default {
  name: "BPInstanceImage",

  props: {
    flowInstanceId: Number,
    flowId: Number,
    flowUserImageWidth: Number,
    flowUserImageHeight: Number,
    flowUserImageZoomable: Boolean,
    pauseUpdates: Boolean,
  },

  data() {
    return {
      bpMap: undefined,
      statusLayer: undefined,
      selectedVertexId: -1,
      timerId: undefined,
      statuses: [],
      mapStyle: undefined,
      eventSource: undefined,
      sseRetryCount: 0,
      sseReconnectTimer: undefined,
    };
  },

  watch: {
    flowId: {
      immediate: false,

      handler(oldValue, newValue) {
        if (oldValue === newValue) return;
        this.initMapWhenHeightSet();
      },
    },
  },

  methods: {
    initMapWhenHeightSet() {
      const expectedHeight = Math.min(550, this.flowUserImageHeight) + 50;
      this.mapStyle = `height: ${expectedHeight}px;`;
      const el = document.getElementById("bp");
      const checkHeight = () => {
        if (el && el.offsetHeight === expectedHeight) {
          this.initMap();
        } else {
          requestAnimationFrame(checkHeight);
        }
      };
      requestAnimationFrame(checkHeight);
    },

    drawTypeLayer(taskTypeIconLayer, bpMap, positions) {
      taskTypeIconLayer.clearLayers();

      this.createTypeMarkers(bpMap, positions).forEach((marker) => {
        taskTypeIconLayer.addLayer(marker);
      });
      taskTypeIconLayer.addTo(bpMap);
    },

    createTypeMarkers(bpMap, positions) {
      const markers = [];
      const maxZoom = bpMap.getMaxZoom();
      const zoomNumber = maxZoom - bpMap.getZoom();
      const size = 32 / (zoomNumber + 1);

      positions.forEach((position) => {
        if (position.taskIconUrl) {
          markers.push(
            this.createMarker(
              position.taskIconUrl,
              position.x,
              position.y2,
              size,
              bpMap
            )
          );
        }
      });
      return markers;
    },

    async getPositions(flowId) {
      return await get("/flow/positions/" + flowId).catch((error) => {
        store.commit("showError", error);
      });
    },

    async getZoomLevel(flowId) {
      return await get("/flow/zoom-level/" + flowId).catch((error) => {
        store.commit("showError", error);
      });
    },

    createMarker(iconUrl, x, y, size, bpMap) {
      const taskIcon = leaflet.icon({
        iconUrl: iconUrl,
        iconSize: [size, size],
      });
      const coords = bpMap.unproject([x, y], bpMap.getMaxZoom());

      return leaflet.marker(coords, { icon: taskIcon });
    },

    drawRectangleLayer(rectangleLayer, bpMap, positions) {
      rectangleLayer.clearLayers();

      this.createRectangles(rectangleLayer, bpMap, positions).forEach(
        (rectangle) => {
          rectangleLayer.addLayer(rectangle);
        }
      );
      rectangleLayer.addTo(bpMap);
    },

    createRectangles(rectangleLayer, bpMap, positions) {
      const rectangles = [];
      const maxZoom = bpMap.getMaxZoom();

      positions.forEach((position) => {
        const topLeft = bpMap.unproject([position.x, position.y], maxZoom);
        const bottomRight = bpMap.unproject(
          [position.x2, position.y2],
          maxZoom
        );
        const options = this.createVertexOptions(
          this.selectedVertexId,
          position.vertexId
        );

        rectangles.push(
          this.createRectangle(
            topLeft,
            bottomRight,
            options,
            position.vertexId,
            rectangleLayer,
            bpMap,
            positions
          )
        );
      });
      return rectangles;
    },

    createRectangle(
      topLeft,
      bottomRight,
      options,
      vertexId,
      rectangleLayer,
      bpMap,
      positions
    ) {
      const bounds = leaflet.latLngBounds(topLeft, bottomRight);
      const rectangle = leaflet.rectangle(bounds, options);

      rectangle.on("click", () => {
        this.$emit("click", vertexId);
        this.selectedVertexId = vertexId;
        this.drawRectangleLayer(rectangleLayer, bpMap, positions);
      });
      return rectangle;
    },

    createVertexOptions(selectedVertexId, vertexId) {
      let options;

      if (selectedVertexId === vertexId) {
        options = {
          weight: 2,
          fill: false,
          opacity: 1,
        };
      } else {
        options = {
          weight: 0,
          fill: true,
          opacity: 1,
          fillOpacity: 0,
        };
      }
      return options;
    },

    connectSse(statusLayer, map, flowInstanceId) {
      this.closeSse();
      this.updateStatusLayer(statusLayer, map, flowInstanceId, false);

      let url =
        baseUrl + "/flow-instance/statuses/" + flowInstanceId + "/stream";
      const token = store.state.keycloak?.token;
      if (token) {
        url += "?token=" + encodeURIComponent(token);
      }
      const es = new EventSource(url);
      this.eventSource = es;

      es.addEventListener("status-update", (event) => {
        this.sseRetryCount = 0;
        const newStatuses = JSON.parse(event.data);
        if (!this.pauseUpdates) {
          if (!this.statusesEqual(this.statuses, newStatuses)) {
            this.drawStatusLayer(newStatuses, statusLayer, map);
          }
        }
        this.statuses = newStatuses;
      });

      es.onerror = () => {
        this.closeSse();
        this.sseRetryCount++;
        if (this.sseRetryCount <= 3) {
          const delay = Math.pow(2, this.sseRetryCount - 1) * 1000;
          this.sseReconnectTimer = window.setTimeout(() => {
            this.connectSse(statusLayer, map, flowInstanceId);
          }, delay);
        } else {
          this.continuouslyUpdateStatuses(statusLayer, map, flowInstanceId);
        }
      };
    },

    closeSse() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = undefined;
      }
      clearTimeout(this.sseReconnectTimer);
    },

    continuouslyUpdateStatuses(statusLayer, map, flowInstanceId) {
      this.timerId = window.setTimeout(async () => {
        if (!this.pauseUpdates) {
          this.updateStatusLayer(statusLayer, map, flowInstanceId, false);
        }
        this.continuouslyUpdateStatuses(statusLayer, map, flowInstanceId);
      }, 1000);
    },

    updateStatusLayer(statusLayer, map, flowInstanceId, zoomChanged) {
      this.getStatuses(flowInstanceId).then((newStatuses) => {
        // We don't update the status layer if the statuses are the same, except when the zoomlevel changed because in that case the size of the markers changes
        if (zoomChanged || !this.statusesEqual(this.statuses, newStatuses)) {
          this.drawStatusLayer(newStatuses, statusLayer, map);
          this.statuses = newStatuses;
        }
      });
    },

    statusesEqual(statuses, newStatuses) {
      if (statuses?.length != newStatuses?.length) {
        return false;
      }
      for (let i = 0; i < statuses?.length; ++i) {
        if (statuses[i].statusIconUrl != newStatuses[i].statusIconUrl) {
          return false;
        }
        if (statuses[i].warningIconUrl != newStatuses[i].warningIconUrl) {
          return false;
        }
        if (statuses[i].x != newStatuses[i].x) {
          return false;
        }
        if (statuses[i].y != newStatuses[i].y) {
          return false;
        }
        if (statuses[i].x2 != newStatuses[i].x2) {
          return false;
        }
        if (statuses[i].y2 != newStatuses[i].y2) {
          return false;
        }
      }
      return true;
    },

    async getStatuses(flowInstanceId) {
      return await get("/flow-instance/statuses/" + flowInstanceId).catch(
        (error) => {
          store.commit("showError", error);
        }
      );
    },

    drawStatusLayer(statuses, statusLayer, bpMap) {
      statusLayer.clearLayers();

      this.createStatusMarkers(statuses, bpMap).forEach((marker) => {
        statusLayer.addLayer(marker);
      });
      statusLayer.addTo(bpMap);
    },

    createStatusMarkers(statuses, bpMap) {
      const markers = [];
      const maxZoom = bpMap.getMaxZoom();
      const zoomNumber = maxZoom - bpMap.getZoom();
      const statusSize = 32 / (zoomNumber + 1);
      const warningSize = 24 / (zoomNumber + 1);

      if (statuses) {
        statuses.forEach((status) => {
          if (status.statusIconUrl) {
            markers.push(
              this.createMarker(
                status.statusIconUrl,
                status.x2,
                status.y,
                statusSize,
                bpMap
              )
            );
          }
          if (status.warningIconUrl) {
            markers.push(
              this.createMarker(
                status.warningIconUrl,
                status.x,
                status.y,
                warningSize,
                bpMap
              )
            );
          }
        });
      }
      return markers;
    },

    async initMap() {
      if (this.bpMap) {
        this.bpMap.remove();
        this.bpMap = undefined;
      }
      const zoomable = this.flowUserImageZoomable;

      this.bpMap = leaflet
        .map("bp", {
          crs: leaflet.CRS.Simple, // Set a flat projection, as we are projecting an image
          attributionControl: false,
          doubleClickZoom: false,
          scrollWheelZoom: false,
          touchZoom: false,
          dragging: zoomable,
          zoomControl: zoomable,
          zoomDelta: 1,
        })
        .setView([0, 0], 0);

      zoomifyLayer(
        "/flow/tiled-image/" + this.flowId + "/{g}/{z}-{x}-{y}.jpg",
        {
          width: this.flowUserImageWidth,
          height: this.flowUserImageHeight,
          tolerance: 1,
          get: get,
        }
      ).addTo(this.bpMap);

      const zoom = await this.getZoomLevel(this.flowId);
      if (zoom || zoom === 0) {
        this.bpMap.setZoom(zoom);
      }

      const taskTypeIconLayer = new leaflet.FeatureGroup();
      const rectangleLayer = new leaflet.FeatureGroup();
      const positions = await this.getPositions(this.flowId);

      if (positions) {
        this.drawTypeLayer(taskTypeIconLayer, this.bpMap, positions);
        this.drawRectangleLayer(rectangleLayer, this.bpMap, positions);
      }
      if (this.flowInstanceId) {
        this.statusLayer = new leaflet.FeatureGroup();

        this.connectSse(this.statusLayer, this.bpMap, this.flowInstanceId);
      }
      this.bpMap.on("zoomend", () => {
        if (this.flowInstanceId && this.statuses.length) {
          this.drawStatusLayer(this.statuses, this.statusLayer, this.bpMap);
        }
        this.drawTypeLayer(taskTypeIconLayer, this.bpMap, positions);
      });
    },
  },

  mounted() {
    this.initMapWhenHeightSet();
  },

  destroyed() {
    this.closeSse();
    clearTimeout(this.timerId);
  },
};
</script>

<style>
#bp {
  background-color: white;
  z-index: 1;
}
</style>
